import { QueryClientProvider } from '@tanstack/react-query'
import { ReactNode } from 'react'
import { queryClient } from '@/lib/queryClient'
import { OrganizationProvider } from '@/contexts/OrganizationContext'
import { UserProvider } from '@/contexts/UserContext'
import { LocalizationProvider } from '@/contexts/LocalizationContext'
import { PermissionProvider } from '@/contexts/PermissionContext'
import { ProfileSwitchProvider } from '@/contexts/ProfileSwitchContext'
import { NotificationProvider } from '@/contexts/NotificationContext'
import { AbilityProvider } from '@/ability/AbilityContext'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { BehaviorTrackingProvider } from '@/analytics/BehaviorTrackingProvider'
import { useAuthInit } from '@/hooks/useAuthInit'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { Toaster } from 'sonner'

interface ProvidersProps {
  children: ReactNode
}

function AuthInitializer({ children }: { children: ReactNode }) {
  useAuthInit()
  // Fetch full user profile (name, profiles, permissions) after auth init.
  // This ensures header shows the user name instead of phone-only.
  useCurrentUser()
  return <>{children}</>
}

export function Providers({ children }: ProvidersProps) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider>
          <OrganizationProvider>
            <BehaviorTrackingProvider>
            <UserProvider>
              <PermissionProvider>
                <AbilityProvider>
                  <ProfileSwitchProvider>
                    <NotificationProvider
                      enablePolling={true}
                      pollingInterval={2 * 60 * 1000}
                    >
                      <AuthInitializer>
                        {children}
                        <Toaster />
                      </AuthInitializer>
                    </NotificationProvider>
                  </ProfileSwitchProvider>
                </AbilityProvider>
              </PermissionProvider>
            </UserProvider>
            </BehaviorTrackingProvider>
          </OrganizationProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
